import { useTranslation } from 'react-i18next';

import type { ListFilter, TransportChainFixResponse } from '@/api/generated/schemas';
import { ChainFixKind } from '@/api/generated/schemas';
import {
  RegularTransportSelector,
  type RegularTransportRef,
} from '../../../../components/RegularTransportSelector';

interface TransportPickerButtonProps {
  /** Doporučená oprava — řídí popisek, pozici vložení i filtr nabídky. */
  fix: TransportChainFixResponse | null | undefined;
  /** Konec dosavadního řetězu — nabídka se zúží na dopravy, které z něj odlétají. */
  chainEndStationId: number | null;
  /** Dopravy už ve schématu se znovu nenabízejí. */
  excludeIds: number[];
  disabled: boolean;
  onAdd: (regularTransportId: number, insertAt: number | undefined) => void;
}

/**
 * Tlačítko „Přidat dopravu" / „Doplnit chybějící dopravu".
 *
 * Filtr nabídky se počítá z **aktuálního `fix`**, ne z odpovědi na uložení — jinak by selector nabízel
 * podle zastaralého stavu. Když je řetěz rozbitý, má tlačítko jen červený obrys a text nad průhledným
 * pozadím (ne plný `variant="danger"`).
 */
export function TransportPickerButton({
  fix,
  chainEndStationId,
  excludeIds,
  disabled,
  onAdd,
}: TransportPickerButtonProps) {
  const { t } = useTranslation('venua-planner');

  const filters: ListFilter[] = [{ field: 'air' }];
  if (excludeIds.length > 0) {
    filters.push({ field: 'excludeIds', ids: excludeIds.map(String) });
  }

  if (fix?.kind === ChainFixKind.BorderOutbound) {
    filters.push({ field: 'originDomestic' });
  } else if (fix?.kind === ChainFixKind.BorderInbound) {
    filters.push({ field: 'destinationDomestic' });
    if (fix.fromStationId != null) {
      filters.push({ field: 'fromStationId', number: fix.fromStationId });
    }
  } else if (fix?.kind === ChainFixKind.Link) {
    if (fix.fromStationId != null) {
      filters.push({ field: 'fromStationId', number: fix.fromStationId });
    }
    if (fix.toStationId != null) {
      filters.push({ field: 'toStationId', number: fix.toStationId });
    }
  } else if (chainEndStationId != null) {
    filters.push({ field: 'fromStationId', number: chainEndStationId });
  }

  return (
    // Rozbitý řetěz: jen červený obrys a text nad průhledným pozadím, ne plný `variant="danger"`.
    <div
      className={
        fix
          ? '[&_button]:border-phx-danger [&_button]:text-phx-danger [&_button]:bg-transparent'
          : undefined
      }
    >
      <RegularTransportSelector
        mode="single"
        trigger="button"
        addMode
        label={t('transportScheme.picker.label')}
        buttonVariant={fix ? 'outline' : 'secondary'}
        buttonSize="sm"
        buttonLabel={t(fix ? 'transportScheme.picker.fix' : 'transportScheme.picker.add')}
        extraFilters={filters}
        selected={[]}
        onChange={(items: readonly RegularTransportRef[]) => {
          const picked = items[0];
          if (picked && !disabled) {
            onAdd(picked.regularTransportId, fix?.insertAt ?? undefined);
          }
        }}
      />
    </div>
  );
}
